import { BaseStats, EventIds, PerformanceEvent, ErrorInfo, BaseEvent, ErrorType } from '@fpkg/stats-core';
import { getNanoId, getQueryFromURL } from '@fpkg/stats-utils';
import { getBrowserEnv, isMobile, isWeChatMiniApp } from '@fpkg/browser-env';
import packageJson from '../package.json';
import WebStatsConfig, { DefaultAPPCodeForSFAPM, DefaultTestAPPCodeForSFAPM } from './WebStatsConfig';
import { getCookie, setCookie } from './utils';
import JsErrorPlugin from './plugins/JsErrorPlugin';
import ResourceErrorPlugin from './plugins/ResourceErrorPlugin';
import BlankScreenPlugin from './plugins/BlankScreenPlugin';
import AjaxPlugin from './plugins/AjaxPlugin';
import PerformancePlugin from './plugins/PerformancePlugin';
import ModuleExposurePlugin from './plugins/ModuleExposurePlugin';
import PageViewPlugin from './plugins/PageViewPlugin';
import AcspPlugin from './plugins/AcspPlugin';
import SFAPMPlugin, { SFAPMCustomErrorType } from './plugins/SFAPMPlugin';
import WebSender from './WebSender';
import SFLogsPlugin from './plugins/SFLogsPlugin';
import { DetectBlankScreenOpts } from './data-capture-funcs/blankScreen';

export default class WebStats extends BaseStats {
  sdkVersion: string = packageJson.version;

  config: WebStatsConfig = new WebStatsConfig();

  sender: WebSender = new WebSender();

  /**
   * 设置神策实例，神策js异步加载成功时调用
   * @param sensors
   */
  handlerSensorsJsLoaded(sensors: any) {
    this.sensors = sensors;
    this.sender.handlerSensorsJsLoaded(sensors);
  }

  /**
   * 获取平台类型，丰景台统计用到，没有区分字节小程序、阿里小程序
   */
  getPlatformType(): string {
    if (isWeChatMiniApp()) {
      // 微信小程序，产品希望小程序webview也统计到小程序渠道
      return '小程序';
    }
    if (isMobile()) {
      // 移动端
      return 'H5';
    }
    // pc
    return 'PC';
  }

  /**
   * 获取平台类型名称，包括微应用、各种小程序
   */
  getPlatformName(): string {
    return getBrowserEnv();
  }

  /**
   * 构建默认插件
   */
  buildDefaultPlugins() {
    const pluginsOpts = this.config.plugins || {};
    // SF APM插件
    // 修正默认配置
    if (pluginsOpts.sfapm === true) {
      // 配置为 true 情况
      pluginsOpts.sfapm = {
        appCode: this.config.platform_env === 'sit' ? DefaultTestAPPCodeForSFAPM : DefaultAPPCodeForSFAPM,
      };
    } else if (
      pluginsOpts.sfapm &&
      typeof pluginsOpts.sfapm === 'object' &&
      (pluginsOpts.sfapm as any).appCode === DefaultAPPCodeForSFAPM &&
      this.config.platform_env === 'sit'
    ) {
      // 使用默认配置情况，测试环境修正默认值
      (pluginsOpts.sfapm as any).appCode = DefaultTestAPPCodeForSFAPM;
    } else {
      // 其他情况以用户配置为准
    }
    this.addPlugin(new SFAPMPlugin(pluginsOpts.sfapm), pluginsOpts.sfapm);

    // js错误插件
    const jsErrorPlugin = new JsErrorPlugin(pluginsOpts.jsError);
    this.addPlugin(jsErrorPlugin, pluginsOpts.jsError);
    if (pluginsOpts.jsError) {
      // 暴露插件类的方法
      this.reportJsErrorEvent = (event: Event, microApp?: string): ErrorInfo | null =>
        jsErrorPlugin.handlerJsErrorEvent(event, microApp);
      this.reportUnhandledrejectionEvent = (event: PromiseRejectionEvent, microApp?: string): ErrorInfo | null =>
        jsErrorPlugin.handlerUnhandledrejectionEvent(event, microApp);
      this.reportJsError = (error: Error, microApp?: string): ErrorInfo | null =>
        jsErrorPlugin.handleJsError(error, microApp, ErrorType.ManualHandleJsError);
    }

    // 资源加载异常插件
    const resourceErrorPlugin = new ResourceErrorPlugin(pluginsOpts.resourceError);
    this.addPlugin(resourceErrorPlugin, pluginsOpts.resourceError);
    if (resourceErrorPlugin) {
      this.reportResourceError = (event: Event, microApp?: string) =>
        resourceErrorPlugin.captureResourceError(event, microApp);
    }

    // 白屏检测插件
    const blankScreenPlugin = new BlankScreenPlugin(pluginsOpts.blankScreen);
    this.addPlugin(blankScreenPlugin, pluginsOpts.blankScreen);
    if (pluginsOpts.blankScreen) {
      this.detectBlankScreen = (info: BaseEvent, option: DetectBlankScreenOpts, microApp?: string) =>
        blankScreenPlugin.detectBlankScreen(info, option, microApp);
    }

    // 性能监控插件
    const performancePlugin = new PerformancePlugin(pluginsOpts.performance);
    this.addPlugin(performancePlugin, pluginsOpts.performance);
    if (pluginsOpts.performance) {
      this.reportPerformance = (performanceInfo: PerformanceEvent, microApp?: string) => {
        performancePlugin.handlerEvent(performanceInfo, microApp);
      };
    }

    // 模块曝光插件
    this.addPlugin(new ModuleExposurePlugin(pluginsOpts.moduleExposure), pluginsOpts.moduleExposure);

    // PV 插件
    const pageViewPlugin = new PageViewPlugin(pluginsOpts.pageView);
    this.addPlugin(pageViewPlugin, pluginsOpts.pageView);
    if (pluginsOpts.pageView) {
      this.reportPageView = (url_path: string, microApp?: string) => pageViewPlugin.reportPageView(url_path, microApp);
      this.reportPageLeave = (url_path: string, duration: number, microApp?: string) =>
        pageViewPlugin.reportPageLevel(url_path, duration, microApp);
    }

    // 企服专用插件
    this.addPlugin(new AcspPlugin(pluginsOpts.acsp as { isMobile?: boolean }), pluginsOpts.acsp);

    // 顺丰云日志插件
    this.addPlugin(new SFLogsPlugin(pluginsOpts.sflogs), pluginsOpts.sflogs);

    // 请求监控插件
    // 由于需要获取SFAPMPlugin、SFLogsPlugin插件的options，请求监控插件放在下面
    const ajaxPlugin = new AjaxPlugin(pluginsOpts.ajax);
    this.addPlugin(ajaxPlugin, pluginsOpts.ajax);
    if (pluginsOpts.ajax) {
      this.initAjaxCapture = (ajaxObj: typeof XMLHttpRequest, microApp?: string, location?: Location) => {
        ajaxPlugin.initAjaxCapture(ajaxObj, microApp, location);
      };
    }
  }

  /**
   * 设置渠道号，init方法需要调用一次，如果想自定义设置调用此方法
   * @param channelCode 如果为空，会根据url参数自动获取渠道号
   */
  setChannelCode(channelCodeNew?: string): void {
    let channelCode = channelCodeNew;
    if (channelCode !== undefined && channelCode !== null) {
      this.channelCode = channelCode || '';
      sessionStorage.setItem('channelCode', this.channelCode);
      return;
    }

    const { channel_keys } = this.config || {};
    if (!Array.isArray(channel_keys) || channel_keys.length === 0) return;

    for (let i = 0; i < channel_keys.length; i++) {
      channelCode = getQueryFromURL(channel_keys[i]) || '';
      if (channelCode) {
        this.channelCode = channelCode;
        sessionStorage.setItem('channelCode', this.channelCode);
        break;
      }
    }
  }

  /**
   * 生成设备唯一号，清除缓存会变
   * @private
   */
  createNanoid(): string {
    let nanoId = getQueryFromURL('nanoid') || '';
    if (nanoId) {
      // 如果url参数有设备id,重置本地设备id,通常小程序打开webview会传设备id,统一两个环境设备id
      setCookie('nanoid', nanoId, 30 * 365);
      return nanoId;
    }

    nanoId = getCookie('nanoid');
    if (nanoId) {
      return nanoId;
    }

    nanoId = getNanoId();
    setCookie('nanoid', nanoId, 30 * 365);

    return nanoId;
  }

  /**
   * 生产会话id
   * @private
   */
  createSessionid(): string {
    // 如果存在预收集对象，使用预收集对象的会话id
    // @ts-ignore
    if (window.StatsPreCollector && window.StatsPreCollector.session_id) {
      // @ts-ignore
      return window.StatsPreCollector.session_id;
    }
    return getNanoId();
  }

  /**
   * 页面404事件,在404路由页面组件mounted生命周期中调用
   * @param params 其他自定义属性，如果需要上报顺丰云日志，设置自定义属性event_level='error'。
   */
  pageNotFound(params: object = {}): void {
    const eventInfo = {
      event: EventIds.PageNotFound,
      event_name: '页面404',
      $url: window.location.href, // 当前地址
      $referer: window.document.referrer, // 上一次地址
      ...params,
    };
    this.event(eventInfo);
  }

  reportCustomErrorToSFAPM(params: SFAPMCustomErrorType): void {
    if (this.plugins && this.plugins.sfapm) {
      (this.plugins.sfapm as SFAPMPlugin).reportCustomErrorToSFAPM(params);
    } else {
      console.warn('未配置APM组件，不能上报APM');
    }
  }

  reportPerformance(performanceInfo: PerformanceEvent) {
    this.event({
      ...performanceInfo,
      event: EventIds.WebPerformance,
    });
  }

  reportPageView(url_path: string) {
    this.event({ event: EventIds.PageView, visible: true, url_path });
  }

  reportPageLeave(url_path: string, duration: number) {
    this.event({
      event: EventIds.PageLeave,
      visible: false,
      url_path,
      duration,
    });
  }

  // @ts-ignore
  reportJsErrorEvent(errorInfo: ErrorEvent) {}

  // @ts-ignore
  reportUnhandledrejectionEvent(errorInfo: PromiseRejectionEvent) {}

  // @ts-ignore
  reportJsError(error: Error, microApp?: string) {}

  // @ts-ignore
  initAjaxCapture(ajaxObj: typeof XMLHttpRequest) {}

  // @ts-ignore
  reportResourceError(info: Event) {}

  // @ts-ignore
  detectBlankScreen(errorInfo: BaseEvent, option: DetectBlankScreenOpts) {}
}
